import {ReactNode, useMemo} from 'react';

import {CrmToFrontCustomFieldMapping} from '../../../../../../../../../src/components/accounts/crm/common/crmSyncHelpers';
import {CrmFieldModel} from '../../../../../../../../../src/models/api/crmFieldModel';
import {CrmSyncSourcesEnum} from '../../../../../../../../../src/models/api/crmSyncModel';
import {IconName} from '../../../../../../../../shared/ui/src/components/icon/icon';
import {CrmSyncOptionsEnum, SelectedOption} from '../crmSyncsOptions/crmSyncsOptions';
import {hubspotConfig} from './hubspotPageConfig';
import {microsoftDynamicsConfig} from './microsoftDynamicsPageConfig';
import {outlookDirectoryConfig} from './outlookDirectoryPageConfig';
import {salesforceConfig} from './salesforcePageConfig';

export interface CrmSyncsPageConfig {
  logo: string;
  logoSm: string;
  logoTiny: string;
  displayName: string;
  loginDescription?: ReactNode;
  infoBoxDescription?: ReactNode;
  account?: CrmSyncsPageResourceConfig;
  card?: CrmSyncsPageResourceConfig;
}

export interface CrmSyncsPageResourceConfig {
  // General
  customTitle?: ReactNode;
  customDescription?: ReactNode;
  customIconName?: IconName;
  helpCenterLink: string;
  maxNumberToSync: number;
  buildExternalUrl: (baseUrl: string, id: string) => string;
  // Sync Options like lists, views, etc
  syncOptionsTitle: ReactNode;
  syncOptions: CrmSyncsResourceOptions;
  // Mapping external fields/attributes to Front fields
  syncMapping: {
    computeRequiredMapping: (selectedOption?: SelectedOption) => ReadonlyArray<CrmToFrontCustomFieldMapping>;
    computeOptionalMapping: (selectedOption?: SelectedOption) => ReadonlyArray<CrmToFrontCustomFieldMapping>;
    computeCrmDefaultFieldIcon: (crmField?: CrmFieldModel) => IconName | undefined;
    computeCrmDefaultFieldPlaceholderIcon: (crmFieldId: string) => IconName | undefined;
    computeCrmDefaultFieldPlaceholderName: (crmFieldId: string) => string | undefined;
    renderSpecialFieldMessage?: (frontFieldId?: number) => ReactNode | undefined;
    filterCrmFields?: (crmFields?: ReadonlyArray<CrmFieldModel>) => ReadonlyArray<CrmFieldModel> | undefined;
    defaultCrmFieldType: string;
    customTitle?: ReactNode;
    customDescription?: ReactNode;
  };
}

export type CrmSyncsResourceOptions = {
  [key in CrmSyncOptionsEnum]?: {
    optionText: ReactNode;
    tooltip?: ReactNode;
    dropdownPlaceholder?: ReactNode;
    dropdownTitle?: ReactNode;
    learnMoreLink?: string;
    maxCountError?: ReactNode;
  };
};

function getCrmPageConfig(source: CrmSyncSourcesEnum): CrmSyncsPageConfig {
  switch (source) {
    case CrmSyncSourcesEnum.MICROSOFT_DYNAMICS:
      return microsoftDynamicsConfig;
    case CrmSyncSourcesEnum.HUBSPOT:
      return hubspotConfig;
    case CrmSyncSourcesEnum.OUTLOOK_DIRECTORY:
      return outlookDirectoryConfig;
    case CrmSyncSourcesEnum.SALESFORCE:
    default:
      return salesforceConfig;
  }
}

export function useCrmPageConfig(source: CrmSyncSourcesEnum) {
  return useMemo(() => getCrmPageConfig(source), [source]);
}
